import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, FilterBar, PageHeader, SplitDetailPanel, StatusBadge } from '../components/ui';
import { mapStatusToBadge } from '../lib/status';

type VerificationStatus = 'verified' | 'suspicious' | 'failed' | 'anchored';

interface VerificationEvent {
  id: string;
  timestamp: string;
  productName: string;
  batchId: string;
  qrId: string;
  status: VerificationStatus;
  riskScore: number;
  location: string;
  geo: string;
  deviceHash: string;
  ipHash: string;
  anchorRef: string;
  anomalyScore: number;
  channel: string;
}

const verificationEvents: VerificationEvent[] = [
  {
    id: 'VER-20260220-001',
    timestamp: '2026-02-20T07:42:00Z',
    productName: 'Pure Ghee 500ml',
    batchId: 'BT-2026-011',
    qrId: 'QR-7A19-0EE2',
    status: 'verified',
    riskScore: 8,
    location: 'Dallas, US',
    geo: '32.7767, -96.7970',
    deviceHash: 'DEV-a13d8f-2c44',
    ipHash: 'IP-09f12d7a',
    anchorRef: '0x2e31f9...9bc1',
    anomalyScore: 0.06,
    channel: 'Consumer App',
  },
  {
    id: 'VER-20260220-002',
    timestamp: '2026-02-20T08:10:00Z',
    productName: 'Pure Ghee 1L',
    batchId: 'BT-2026-011',
    qrId: 'QR-4C71-9BD0',
    status: 'anchored',
    riskScore: 16,
    location: 'Austin, US',
    geo: '30.2672, -97.7431',
    deviceHash: 'DEV-f281aa-31d0',
    ipHash: 'IP-129f0bc2',
    anchorRef: '0x91a2c4...71fe',
    anomalyScore: 0.18,
    channel: 'Retail Scanner',
  },
  {
    id: 'VER-20260220-003',
    timestamp: '2026-02-20T08:34:00Z',
    productName: 'Herbal Shampoo 200ml',
    batchId: 'BT-2026-006',
    qrId: 'QR-93D4-7AF0',
    status: 'suspicious',
    riskScore: 74,
    location: 'Phoenix, US',
    geo: '33.4484, -112.0740',
    deviceHash: 'DEV-fc3910-abc1',
    ipHash: 'IP-91c51a01',
    anchorRef: '0x02fd87...993a',
    anomalyScore: 0.82,
    channel: 'Consumer App',
  },
  {
    id: 'VER-20260220-004',
    timestamp: '2026-02-20T09:05:00Z',
    productName: 'Tea Tree Soap Pack',
    batchId: 'BT-2026-008',
    qrId: 'QR-712C-1CE3',
    status: 'failed',
    riskScore: 91,
    location: 'Houston, US',
    geo: '29.7604, -95.3698',
    deviceHash: 'DEV-3bc77a-004d',
    ipHash: 'IP-0f113cb4',
    anchorRef: '0xd2ac10...4f87',
    anomalyScore: 0.95,
    channel: 'Retail Scanner',
  },
  {
    id: 'VER-20260220-005',
    timestamp: '2026-02-20T09:44:00Z',
    productName: 'Pure Ghee 500ml',
    batchId: 'BT-2026-011',
    qrId: 'QR-A1D3-48BC',
    status: 'verified',
    riskScore: 5,
    location: 'San Jose, US',
    geo: '37.3382, -121.8863',
    deviceHash: 'DEV-81bced-cf28',
    ipHash: 'IP-ff12cd30',
    anchorRef: '0x5d883b...7a10',
    anomalyScore: 0.04,
    channel: 'Consumer App',
  },
  {
    id: 'VER-20260220-006',
    timestamp: '2026-02-20T10:11:00Z',
    productName: 'Herbal Shampoo 200ml',
    batchId: 'BT-2026-006',
    qrId: 'QR-D19E-0C44',
    status: 'anchored',
    riskScore: 24,
    location: 'Denver, US',
    geo: '39.7392, -104.9903',
    deviceHash: 'DEV-99f10a-aa9f',
    ipHash: 'IP-447bb09d',
    anchorRef: '0x7fd0aa...bb81',
    anomalyScore: 0.12,
    channel: 'Warehouse App',
  },
];

const toDateTime = (value: string) =>
  new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const riskToneFromScore = (riskScore: number): 'active' | 'suspicious' | 'recalled' => {
  if (riskScore >= 80) {
    return 'recalled';
  }
  if (riskScore >= 50) {
    return 'suspicious';
  }
  return 'active';
};

export default function VerificationLogsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'latest' | 'earliest' | 'risk-high' | 'risk-low'>('latest');
  const [selectedId, setSelectedId] = useState<string>(verificationEvents[0]?.id ?? '');

  const statusOptions = useMemo(
    () => [
      { value: 'all', label: 'All', count: verificationEvents.length },
      ...(['verified', 'anchored', 'suspicious', 'failed'] as VerificationStatus[]).map((status) => ({
        value: status,
        label: mapStatusToBadge(status).label,
        count: verificationEvents.filter((event) => event.status === status).length,
      })),
    ],
    []
  );

  const filteredEvents = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filtered = verificationEvents.filter((event) => {
      const matchesStatus = statusFilter === 'all' || event.status === statusFilter;
      const matchesSearch =
        normalizedSearch === '' ||
        event.productName.toLowerCase().includes(normalizedSearch) ||
        event.batchId.toLowerCase().includes(normalizedSearch) ||
        event.qrId.toLowerCase().includes(normalizedSearch);
      return matchesStatus && matchesSearch;
    });

    return filtered.sort((a, b) => {
      if (sortBy === 'earliest') {
        return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
      }
      if (sortBy === 'risk-high') {
        return b.riskScore - a.riskScore;
      }
      if (sortBy === 'risk-low') {
        return a.riskScore - b.riskScore;
      }
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });
  }, [searchTerm, statusFilter, sortBy]);

  useEffect(() => {
    if (filteredEvents.length === 0) {
      setSelectedId('');
      return;
    }

    if (!filteredEvents.some((event) => event.id === selectedId)) {
      setSelectedId(filteredEvents[0].id);
    }
  }, [filteredEvents, selectedId]);

  const selectedEvent = filteredEvents.find((event) => event.id === selectedId);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <PageHeader
        title="Verification Logs"
        description="Track verification events with risk signals, location, and blockchain references."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(isAdminRoute ? '/admin' : '/manufacturer')}
            >
              Back to Dashboard
            </Button>
            <Button variant="outline">Export CSV</Button>
          </div>
        }
      />

      <FilterBar
        className="mt-6"
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search by product, batch, or QR..."
        statusOptions={statusOptions}
        selectedStatus={statusFilter}
        onStatusChange={setStatusFilter}
        sortOptions={[
          { value: 'latest', label: 'Latest First' },
          { value: 'earliest', label: 'Earliest First' },
          { value: 'risk-high', label: 'Risk (High to Low)' },
          { value: 'risk-low', label: 'Risk (Low to High)' },
        ]}
        selectedSort={sortBy}
        onSortChange={(value) => setSortBy(value as 'latest' | 'earliest' | 'risk-high' | 'risk-low')}
      />

      <SplitDetailPanel
        className="mt-4"
        listTitle={`Events (${filteredEvents.length})`}
        detailTitle={selectedEvent ? `Event ${selectedEvent.id}` : 'Event Details'}
        list={
          filteredEvents.length === 0 ? (
            <p className="p-4 text-sm text-gray-500">No verification events match the current filters.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredEvents.map((event) => {
                const status = mapStatusToBadge(event.status);
                const active = selectedId === event.id;
                return (
                  <button
                    key={event.id}
                    type="button"
                    onClick={() => setSelectedId(event.id)}
                    className={`w-full px-4 py-3 text-left transition-colors ${
                      active ? 'bg-emerald-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{event.productName}</p>
                        <p className="text-xs text-gray-500">{event.batchId}</p>
                      </div>
                      <StatusBadge tone={status.tone} size="sm">
                        {status.label}
                      </StatusBadge>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                      <span>{toDateTime(event.timestamp)}</span>
                      <span>Risk {event.riskScore}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )
        }
        detail={
          selectedEvent ? (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge {...mapStatusToBadge(selectedEvent.status)} />
                <StatusBadge tone={riskToneFromScore(selectedEvent.riskScore)}>
                  Risk {selectedEvent.riskScore}
                </StatusBadge>
                <StatusBadge tone={selectedEvent.anomalyScore >= 0.8 ? 'ai-flagged' : 'muted'}>
                  AI {selectedEvent.anomalyScore.toFixed(2)}
                </StatusBadge>
              </div>

              <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                <div className="rounded-lg bg-gray-50 p-3">
                  <dt className="text-xs uppercase text-gray-500">Product</dt>
                  <dd className="mt-1 font-medium text-gray-900">{selectedEvent.productName}</dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <dt className="text-xs uppercase text-gray-500">QR ID</dt>
                  <dd className="mt-1 font-medium text-gray-900">{selectedEvent.qrId}</dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <dt className="text-xs uppercase text-gray-500">Batch</dt>
                  <dd className="mt-1 font-medium text-gray-900">{selectedEvent.batchId}</dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <dt className="text-xs uppercase text-gray-500">Timestamp</dt>
                  <dd className="mt-1 font-medium text-gray-900">{toDateTime(selectedEvent.timestamp)}</dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <dt className="text-xs uppercase text-gray-500">Location</dt>
                  <dd className="mt-1 font-medium text-gray-900">{selectedEvent.location}</dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <dt className="text-xs uppercase text-gray-500">Geo Coordinates</dt>
                  <dd className="mt-1 font-medium text-gray-900">{selectedEvent.geo}</dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <dt className="text-xs uppercase text-gray-500">Device Hash</dt>
                  <dd className="mt-1 font-mono text-xs text-gray-900">{selectedEvent.deviceHash}</dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <dt className="text-xs uppercase text-gray-500">IP Hash</dt>
                  <dd className="mt-1 font-mono text-xs text-gray-900">{selectedEvent.ipHash}</dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-3 md:col-span-2">
                  <dt className="text-xs uppercase text-gray-500">Blockchain Anchor</dt>
                  <dd className="mt-1 font-mono text-xs text-gray-900">{selectedEvent.anchorRef}</dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-3 md:col-span-2">
                  <dt className="text-xs uppercase text-gray-500">Verification Channel</dt>
                  <dd className="mt-1 font-medium text-gray-900">{selectedEvent.channel}</dd>
                </div>
              </dl>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Select an event from the list to view details.</p>
          )
        }
      />
    </div>
  );
}
